import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { FormControl, FormGroup } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, convertToParamMap, Params } from '@angular/router';
import * as moment from 'moment';
import { forkJoin } from 'rxjs';
import { Observable, merge } from 'rxjs';
import { tap, delay } from 'rxjs/operators';
import { Station } from '../core';
import { Filter, ProfileModelService } from '../profile/profile-model.service';
import { ProfileService } from '../profile/profile.service';
import { BaseSearchDialogComponent } from '../search-dialog/base-search-dialog.component';
import { DialogData } from '../search-dialog/search-dialog.component';
import { SearchFilterAggregate, SearchFiltersService } from '../search-filters/search-filters.service';
import { PlaylistService, StationService } from '../core';
import {
  DatePickerComponent,
  MultiDropboxComponent,
  MultiDropboxData,
  PlayerSidenavComponent,
  PlayerSidenavReferencesService
} from '../shared';
import { SearchHitsDataSource, SearchKeywordGrid } from './search-keywords-data-source';
import { ElasticSearchHit, ElasticSearchResult, TranscriptionSearchService } from './transcription-search.service';
import { fromEvent } from 'rxjs';
import { Subscription } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { PlayerHeaderComponent } from 'src/app/shared/player-header';
import { StationUtilsService } from '../shared/utils/station-utils.service';

export interface Keyword {
  name: string;
}

export interface TranscriptFilter {
  keywords: Keyword[];
  markets: MultiDropboxData[];
  stations: MultiDropboxData[];
}

declare var dataLayer;

@Component({
  selector: 'app-search-keywords',
  templateUrl: './search-keywords.component.html',
  styleUrls: ['./search-keywords.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden', display: 'none' })),
      state('expanded', style({ height: '*', visibility: 'visible', display: '' })),
      transition('expanded <=> collapsed', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
  ],
  providers: [TranscriptionSearchService, SearchFiltersService, ProfileService]
})
export class SearchKeywordsComponent extends BaseSearchDialogComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('keywordChipInputBox', { static: true }) keywordChipInputBox: ElementRef<HTMLInputElement>;
  @ViewChild('stationMulti', { static: true }) stationMulti: MultiDropboxComponent;
  @ViewChild('marketMulti', { static: true }) marketMulti: MultiDropboxComponent;
  @ViewChild('datePicker', { static: true }) datePicker: DatePickerComponent;
  @ViewChild('savedFiltersMulti', { static: true }) savedFiltersMulti: MultiDropboxComponent;

  @ViewChild('appPlayerHeader', { static: true })
  private audioPlayer: PlayerHeaderComponent;

  keywords: Keyword[] = [];
  stations: MultiDropboxData[] = [];
  markets: MultiDropboxData[] = [];
  filterToApply: MultiDropboxData;
  noResultMessage: string;
  dataSource: SearchHitsDataSource = new SearchHitsDataSource();
  hideKeywordAt = true;
  // For Keyword Chips
  selectable = true;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  selectedRow: SearchKeywordGrid;
  selectedRowIndex: number;
  myForm: FormGroup;
  START_OFFSET = 0;
  defaultPageSize = 10;
  displayedColumns: string[] = ['station', 'transcription', 'keywordAt', 'date', 'icon'];
  resetPaginator = true;
  hidePlayer = true;
  addOnBlur = true;

  get playerOpen() {
    return this.getPlayerSidenav().isOpened();
  }

  origMarketsList: SearchFilterAggregate[];
  origStationsList: Station[];

  isSafari = false;
  filtersRowHeight = '30px';

  resizeFilters = false;
  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;


  currentUserEmail: string;

  // variables for dlv
  previousStations = [];
  previousMarkets = [];

  // For detail row expansion
  isExpansionDetailRow = (i: number, row: object) => {
    return row.hasOwnProperty('detailRow');
  }

  constructor(
    private route: ActivatedRoute,
    private playlistService: PlaylistService,
    private transcriptionSearchService: TranscriptionSearchService,
    private stationService: StationService,
    private playerSidenavReferenceService: PlayerSidenavReferencesService,
    private searchFiltersService: SearchFiltersService,
    public dialog: MatDialog,
    profileService: ProfileService,
    private titleService: Title,
    profileModelService: ProfileModelService,
    private stationUtilsService: StationUtilsService) {

    super(dialog, profileService, profileModelService);

    this.myForm = new FormGroup({
      keywords: new FormControl(null),
      markets: new FormControl(null),
      stations: new FormControl(null),
      startTime: new FormControl(''),
      endTime: new FormControl('')
    });

    this.isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    this.titleService.setTitle('SIA (Search, Index, Archive) - Transcriptions');
    this.currentUserEmail = this.profileModelService.getProfile().token;
  }

  afterSavedDialog(filterName: string) {
    this.refreshSavedFilters();

    this.filterToApply = {
      id: filterName,
      name: filterName
    } as MultiDropboxData;
    this.resetPaginator = true;
    this.search();
  }

  refreshSavedFilters(): void {
    const savedFilterTranscripts: Filter[] = this.profileModelService.getSearchFilter() || [];
    this.savedFiltersMulti.setList(savedFilterTranscripts.map((filter: Filter) => {
      return {
        id: filter.filterName,
        name: filter.filterName
      } as MultiDropboxData;
    }));
  }

  applySavedFilter(item: MultiDropboxData): void {
    const filterName = item.id;
    const filter: Filter = this.profileModelService.getSearchFilter()
      .find((transcriptFilter: Filter) => transcriptFilter.filterName === filterName);

    if (filter) {
      this.applyFilter(filter.keywords, filter.stations, filter.markets);
    }
  }

  async applyFilter(keywordIds: string[], stationIds: string[], marketIds: string[]): Promise<void> {

    const builtFilter: TranscriptFilter = await this.buildFilterData(keywordIds, stationIds, marketIds);
    this.keywords = builtFilter.keywords;
    this.markets = builtFilter.markets;
    this.stations = builtFilter.stations;

    try {
      if (this.keywords || this.markets || this.stations) {
        dataLayer.push({
          event: 'apply-saved-filter',
          operation: 'apply-saved-filter'
        });
      }
    } catch (e) { }

    if (!(this.keywords.length !== 0
      || this.markets.length !== 0
      || this.stations.length !== 0)) {
      this.applyFilterFromSessionStorage();
    }
    this.loadStations();
    this.resetPaginator = true;
    this.search();
  }

  async buildFilterData(keywordIds: string[], stationIds: string[], marketIds: string[]): Promise<TranscriptFilter> {
    let keywords: Keyword[];
    let stations: MultiDropboxData[];
    let markets: MultiDropboxData[];

    if ((stationIds && stationIds.length > 0) || (marketIds && marketIds.length > 0)) {
      /* The URL will receive only ids as params, but the logic to update the multidropbox
      expects complete objects, so we need to query the stations only to rebuild those objects.
      */
      await forkJoin([
        this.searchFiltersService.loadAllStations(),
        this.searchFiltersService.loadAllMarkets()
      ]).subscribe(
        (values) => {
          const stationsResult: Station[] = values[0];
          const marketsResult: SearchFilterAggregate[] = values[1];
          const validStations = stationsResult.filter((station: Station) => stationIds.indexOf(station.id) >= 0);
          stations = this.stationUtilsService.toMultiDropboxDataGroupedByName(validStations);
          markets = marketsResult.filter((marketFilter: SearchFilterAggregate) => marketIds.indexOf(marketFilter.name) >= 0)
            .map((marketFilter: SearchFilterAggregate) => {
              return {
                id: marketFilter.aggregate,
                name: marketFilter.name
              } as MultiDropboxData;
            });
        },
        (error) => {
          // TODO: Implement the error popup or something to display the issue on load the filters
        }
      );
    }

    keywords = keywordIds.map((keyword: string) => {
      return { name: keyword } as Keyword;
    });

    return {
      keywords: keywords || [],
      stations: stations || [],
      markets: markets || []
    } as TranscriptFilter;
  }

  editTranscriptFilter(item: MultiDropboxData, isHomePage?: boolean): void {
    const filterName = item.id;
    const filter: Filter = this.profileModelService.getSearchFilter()
      .find((transcriptFilter: Filter) => transcriptFilter.filterName === filterName);

    if (filter) {
      this.buildFilterData(filter.keywords, filter.stations, filter.markets)
        .then((transcriptFilter: TranscriptFilter) => {
          this.openEditDialogFilter({
            filterName: filter.filterName,
            oldFilterName: filter.filterName,
            stations: [].concat(transcriptFilter.stations),
            markets: [].concat(transcriptFilter.markets),
            keywords: [].concat(transcriptFilter.keywords),
            origMarketsList: [].concat(this.origMarketsList),
            origStationsList: [].concat(this.origStationsList),
            isHomePage: false
          });
        });
    }
  }

  dialogUpdate(dialogData: DialogData) {
    if (this.profileModelService.getDefaultSearchFilter().filterName === dialogData.oldFilterName) {
      this.profileModelService.setDefaultSearchFilter(dialogData.filterName);
    }

    this.profileModelService.addSearchFilter({
      keywords: dialogData.keywords.map((keyword: Keyword) => keyword.name),
      markets: dialogData.markets.map((marketMulti: MultiDropboxData) => marketMulti.name),
      stations: dialogData.stations.map((stationMulti: MultiDropboxData) => stationMulti.id).flat(),
      filterName: dialogData.filterName
    });

    this.keywords = dialogData.keywords;
    this.markets = dialogData.markets;
    this.stations = dialogData.stations;
    try {
      dataLayer.push({
        event: 'saved-filter',
        operation: 'filter-saved'
      });
    } catch (e) { }
  }

  ngOnInit() {
    this.getPlayerSidenav().title = 'Transcription';
    this.noResultMessage = 'No result found';
    this.loadControls();
    this.refreshSavedFilters();
    this.resetSearchResultsView();
    this.route.queryParams.subscribe((params: Params) => {
      const paramMap = convertToParamMap(params);

      this.paginator.pageIndex = 0;


      // Fill all filters with profile configuration
      const searchFilter: Filter = this.profileModelService.getDefaultSearchFilter();

      const stationParamIds: string[] = paramMap.getAll('stationId');
      const stationIds = (stationParamIds && stationParamIds.length > 0) ?
        stationParamIds.map(String) : searchFilter.stations;
      const marketIds = searchFilter.markets;
      const keywordIds = searchFilter.keywords;

      this.applyFilter(keywordIds, stationIds, marketIds);
    });

    setTimeout(() => this.keywordChipInputBox.nativeElement.focus(), 0);

    // window resize observer
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.subscribe((evt: UIEvent) => {
      const winWidth = (evt.target as Window).innerWidth;
      if (this.playerOpen) {
        if (winWidth > 1400) {
          this.filtersRowHeight = '40px';
        } else if (winWidth < 1400 && winWidth > 1300) {
          this.filtersRowHeight = '40px';
        } else if (winWidth < 1300 && winWidth > 1200) {
          this.filtersRowHeight = '40px';
        } else {
          this.filtersRowHeight = '80px';
        }
      } else {
        this.filtersRowHeight = '35px';
        if (winWidth < 1400) {
          this.filtersRowHeight = '40px';
          this.resizeFilters = true;
        } else {
          this.resizeFilters = false;
        }
      }
      if (window.innerWidth < 1300) {
        document.querySelector('.player-class')['style']['width'] = '340px';
      } else {
        document.querySelector('.player-class')['style']['width'] = '400px';
      }
    });
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
  }

  ngOnDestroy() {
    this.getPlayerSidenav().close('selected');
    this.resizeSubscription$.unsubscribe();
  }

  loadControls() {
    this.loadMarkets();
    this.loadStations();
  }

  loadMarkets() {
    this.searchFiltersService.loadAllMarkets().subscribe((markets: SearchFilterAggregate[]) => {
      this.origMarketsList = markets;
      this.marketMulti.setList(markets.map((marketFilter: SearchFilterAggregate) => {
        return {
          id: marketFilter.aggregate,
          name: marketFilter.name
        };
      }));
    });
  }

  public loadStations() {
    this.searchFiltersService.loadAllStations().pipe(delay(10)).subscribe(stations => {
      this.origStationsList = stations;
      const filterStations = this.markets != null && this.markets.length > 0 ?
        stations.filter((station) => this.markets.reduce(
          (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
        ).includes(station.market)) :
        stations;

      this.stationMulti.setList(filterStations.map((station) => {
        const name: string = station.name || '';
        const frequency: string = station.frequency || '';

        return {
          id: station.id,
          name: `${name} ${frequency}`,
          model: station
        };
      }));
    });
  }

  private pushKeywordList(keywordString: string) {
    keywordString.match(/\w+|"[^"]+"/g).map(key => {
      const isKeyExist = this.keywords.filter(keyword => (keyword.name === key)).length > 0;
      if (!isKeyExist) {
        this.keywords.push({ name: key });
      }
    });
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.resetPaginator = false;
          return this.search();
        })
      ).subscribe();
  }

  toggleRow(row: SearchKeywordGrid, i: number) {
    this.dataSource.rowsObject.forEach((element, index) => {
      if (element.hasOwnProperty('detailRow') && (index !== (i + 1))) {
        element.show = false;
      }
      element.icon = 'chevron_right';
    });
    this.dataSource.rowsObject[i + 1].show = !this.dataSource.rowsObject[i + 1].show;
    this.selectedRowIndex = i;

    if (this.dataSource.rowsObject[i + 1].show) {
      this.dataSource.rowsObject[i].icon = 'expand_more';
      this.openPlayer(row, i);
    } else {
      this.dataSource.rowsObject[i].icon = 'chevron_right';
      // this.hidePlayer = true;
      this.selectedRowIndex = null;
      this.selectedRow = null;
      this.pausePlayer();
    }

  }

  hideMasterRow(i) {
    return this.dataSource.rowsObject[i + 1].show;
  }


  openPlayer(row: SearchKeywordGrid, index: number) {
    if (this.selectedRow && (this.selectedRow.hit === row.hit)) {
      return;
    } else {
      this.selectedRow = row;
      this.selectedRowIndex = index;
      const hit: ElasticSearchHit = row.hit;
      const station: Station = {
        id: String(hit.mmStationId), enterpriseStationId: hit.esId,
        name: hit.stationName, market: hit.market
      } as Station;
      const audioPositionSeconds: Number = row.audioPositionSeconds;
      const rawTranscription: String = row.rawTranscription;
      const playFromStart: boolean = this.hideKeywordAt;
      try {
        dataLayer.push({
          event: 'click-transcription-row-selected',
          operation: 'transcription-selected'
        });
      } catch (e) { }

      this.stationService.findStation(station.id)
        .subscribe((stationDetails: Station) => {
          const startTime = moment.utc(hit.segmentStartTime, 'YYYY-MM-DDTHH:mm:ssa');
          const endTime = moment.utc(hit.segmentEndTime, 'YYYY-MM-DDTHH:mm:ssa');
          const duration = moment.duration(endTime.diff(startTime));
          // For segments of 10s, duration in min must be a float
          const durationInMinutes = duration.asSeconds() >= 60 ? duration.asSeconds() / 60 : duration.asSeconds();

          /*
          const playlistDetails = this.playlistService.buildArchiveURL(
            +station.id,
            startTime.toDate(),
            durationInMinutes // this.DURATION_OF_AUDIO
          );
          */
          const playlistDetails = this.playlistService.buildArchiveURLWithSegmentId(
            +station.id,
            hit.segmentId
          );
          const playlistURL: string = playlistDetails.url;

          this.audioPlayer.rowSelected(stationDetails, playlistURL, true,
            rawTranscription, audioPositionSeconds, playFromStart, hit);
          this.hidePlayer = false;
        });
    }
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
  }

  pausePlayer() {
    this.audioPlayer.pause();
  }

  search() {
    const searchKey: string = this.keywords.map(keyword => keyword.name).join(' ');
    this.hideKeywordAt = !searchKey || searchKey.trim().length === 0;
    const stationsList = this.stations != null ? this.stations.map(station => station.id).flat() : [];
    const stationNames = this.stations != null ? this.stations.map(station => station.name) : [];
    let marketList = this.markets != null ? this.markets.reduce(
      (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
    ) : [];
    const marketNames = this.markets != null ? this.markets.reduce(
      (aggregate, marketFilter) => aggregate.concat(marketFilter.name), []
    ) : [];

    if (!(stationsList.length > 0 || marketList.length > 0 || !this.hideKeywordAt
      || this.myForm.value.startTime || this.myForm.value.endTime)) {
      marketList = this.profileModelService.getDefaultMarkets();
    }
    try {
      // compare previousStations and stationNames and send only the diff
      const diffMarkets = marketNames.length > 0 ? marketNames.filter(x => !this.previousMarkets.includes(x)) : [];
      const diffStations = stationNames.length > 0 ? stationNames.filter(x => !this.previousStations.includes(x)) : [];
      const args = {
        event: 'click-search',
        operation: 'search',
        'User-Id': this.currentUserEmail,
        'search-term': searchKey || '(Empty)',
        'sort-active': this.sort.active,
        'sort-direction': this.sort.direction,
        'start-date': this.myForm.value.startTime ? moment(this.myForm.value.startTime).fromNow() : '(Empty)' || '(Empty)',
        'end-date': this.myForm.value.endTime ? moment(this.myForm.value.endTime).fromNow() : '(Empty)' || '(Empty)'
      };
      if (diffMarkets.length > 0) {
        args['selected-markets'] = diffMarkets;
      }
      if (diffStations.length > 0) {
        args['selected-stations'] = diffStations;
      }
      dataLayer.push(args);
      this.previousMarkets = [...marketNames];
      this.previousStations = [...stationNames];
    } catch (e) { }
    if (this.resetPaginator) {
      this.paginator.firstPage();
    }
    const results: Observable<ElasticSearchResult> = this.transcriptionSearchService.findTranscriptions(
      searchKey,
      stationsList,
      marketList,
      this.myForm.value.startTime || null,
      this.myForm.value.endTime || null,
      this.sort.active,
      this.sort.direction,
      this.paginator.pageIndex,
      this.paginator.pageSize || this.defaultPageSize
    );

    const observable = this.dataSource.loadSearchHits(searchKey, results, this.paginator.pageIndex !== this.START_OFFSET);
    this.saveCurrentFiltersToSessionStorage();
    observable.subscribe((result: ElasticSearchResult) => {
      this.noResultMessage = 'No results found';
      this.resetSearchResultsView();
    });
  }

  private saveCurrentFiltersToSessionStorage() {
    sessionStorage.setItem('markets', JSON.stringify(this.markets));
    sessionStorage.setItem('stations', JSON.stringify(this.stations));
    sessionStorage.setItem('keywords', JSON.stringify(this.keywords));
    sessionStorage.setItem('startTime', this.myForm.value.startTime);
    sessionStorage.setItem('endTime', this.myForm.value.endTime);
  }

  private applyFilterFromSessionStorage() {
    const marketsSess = JSON.parse(sessionStorage.getItem('markets'));
    const stationsSess = JSON.parse(sessionStorage.getItem('stations'));
    const keywordsSess = JSON.parse(sessionStorage.getItem('keywords'));
    const startTime = sessionStorage.getItem('startTime');
    const endTime = sessionStorage.getItem('endTime');

    if (marketsSess) {
      this.markets = marketsSess;
    }

    if (stationsSess) {
      this.stations = stationsSess;
    }

    if (keywordsSess) {
      this.keywords = keywordsSess;
    }

    if (startTime && startTime !== 'null') {
      this.myForm.controls.startTime.setValue(startTime);
    }

    if (endTime && endTime !== 'null') {
      this.myForm.controls.endTime.setValue(endTime);
    }
  }

  // Functions for keyword search chips
  addKeyword(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').replace(/"/g, '').trim()) {
      this.pushKeywordList(value);
    }

    if (input) {
      input.value = '';
    }
    this.resetPaginator = true;
    this.search();
  }

  removeKeyword(keyword: Keyword): void {
    const index = this.keywords.indexOf(keyword);

    if (index >= 0) {
      this.keywords.splice(index, 1);
      this.resetPaginator = true;
      this.search();
    }
    this.keywordChipInputBox.nativeElement.focus();
  }

  resetSearchResultsView(): void {
    this.selectedRow = null;
    this.selectedRowIndex = null;
    this.getPlayerSidenav().close('selected');
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
  }

  removeMarket(market: MultiDropboxData) {
    this.markets = this.markets.filter(item => item !== market);
    this.stations = this.stations.filter(item => !new Set(market.id).has(item.model[0].market));
    this.loadStations();
    this.search();
  }

  removeStation(station: MultiDropboxData) {
    this.stations = this.stations.filter(item => item !== station);
    this.resetPaginator = true;
    this.search();
  }

  removeStartTime() {
    this.myForm.controls.startTime.setValue(null);
    this.search();
  }

  removeEndTime() {
    this.myForm.controls.endTime.setValue(null);
    this.resetPaginator = true;
    this.search();
  }

  clearFilters(event?: any) {
    this.stations = [];
    this.markets = [];
    this.keywords = [];
    this.myForm.reset();
    if (this.datePicker && this.datePicker.clear) {
      this.datePicker.clear();
    }
    this.resetPaginator = true;
    this.saveCurrentFiltersToSessionStorage();
  }

  private getPlayerSidenav(): PlayerSidenavComponent {
    return this.playerSidenavReferenceService.get('right');
  }
}
